// Rapport PDF écrit en HTML (15/10/2026) : les 14 pages du rapport sont
// produites ici, puis imprimées en PDF par Chromium côté serveur (Gotenberg, POST /wizard/rapport-pdf). Le gabarit
// rapport-template.pdf (outils/rapport-gabarit/) ne sert plus que de source des dessins (plus de secours depuis le 19/10/2026) :
// logos, photos, carte, icônes et synoptique sont extraits par outils/rapport-gabarit/extraire.sh (public/rapport/),
// sans leurs textes, qui sont écrits ici (donc modifiables).
// Mise en page relevée sur le gabarit (positions en points, A4 = 595,28 × 841,89 pt, origine en haut à gauche).
// Fonctions pures (aucun appel au navigateur) : testables sans canvas. Mêmes formats que le gabarit rempli
// (point décimal : « 45.0 kWc », « 4.50% / 10 ans », « 13.40 c€/kWh »).

// ── Données ───────────────────────────────────────────────────────────────
export interface PdfReportData {
  projectName: string
  clientName: string
  raisonSociale: string
  clientAddress: string
  cpVille: string
  commercial: string
  agence: string
  telephone: string
  commercialEmail: string
  dateEtude: string
  consoAnnuelle: number
  puissanceAbonnement: string | number | null
  raccordement?: string      // « monophasé » | « triphasé » (Step 3)
  factureAvant: number
  factureApres: number
  zeroInjection?: boolean    // zéro-injection : « Surplus non valorisé » au lieu de « Surplus injecté »
  avertissementsExtrapolation?: { fort: boolean; texte: string }[]  // données de consommation extrapolées (12/10/2026)
  profileSource?: string     // 'pdl' | 'csv' | 'predefined' (Step 3)
  profilPredifini?: string   // clé du profil prédéfini (maison_4pers…)
  typeTarif: string         // 'unique' | 'hchp' | 'saisonnier' | 'hsc' | 'hsc_saisonnier'
  tarifHP: number
  tarifHC: number
  tarifHPHiver: number
  tarifHCHiver: number
  tarifHPEte: number
  tarifHCEte: number
  tarifAbonnement: number
  taxeelec: number
  hasTarifHC: boolean
  puissanceKwc: number
  batteryKwh: number
  cyclesBatterieAn: number
  productionAnnuelle: number
  orientation: string
  hasBattery: boolean
  tauxAutoconso: number
  tauxAutoprod: number
  autoconsoDir: number
  autoconsoBat: number
  pertesBat: number
  surplus: number
  reseau: number
  coutNetTTC: number
  /** Détail de l'investissement (bloc CAPEX de l'écran), même règle de TVA que coutNetTTC */
  coutInstallationPV?: number
  /** Chiffrage au matériel (27/10/2026) : lignes du détail, TVA comprise pour un particulier ; page d'annexe */
  detailMateriel?: { poste: string; libelle: string; montant: number; section?: boolean }[]
  // journées types des saisons (03/11/2026) : jours réels les plus proches du jour moyen (WizardOptimizeResult.journeesTypes)
  journeesTypes?: { saison: string; weekEnd: boolean; critere: string; date: string; production: number[]; achat: number[];
    stock: number[]; consommation: number[] }[]
  // chiffrage au matériel par section (03/11/2026) : prix de vente (TTC pour un particulier), sans prix de revient ni marge
  sectionsCapex?: { section: string; libelle: string; montant: number }[]
  resumeMateriel?: { [cle: string]: string }
  garanties?: string[]
  /** Professionnel chiffré au matériel : « Agricole » ou « ICT (industrie, commerce, tertiaire) » */
  typePro?: string
  coutInstallationBatterie?: number
  coutDatalogger?: number
  coutTravaux?: number
  travauxLibelle?: string
  /** Câble onduleur ↔ TGBT (19/10/2026) : coût (même TVA que l'installation) et description ; sans description, pas de ligne */
  coutCable?: number
  cableLibelle?: string
  /** Coût du kWh solaire affiché à l'écran (c€/kWh) */
  coutKwhSolaire?: number
  maintenanceAnnuelle: number
  clientTelephone?: string
  tarifHSC?: number
  tarifHSCHiver?: number
  tarifHSCEte?: number
  periodesHC?: any[]
  periodesHSC?: any[]
  periodesHCEte?: any[]
  periodesHCHiver?: any[]
  periodesHSCEte?: any[]
  periodesHSCHiver?: any[]
  consoHpKwh?: number
  consoHcKwh?: number
  consoHscKwh?: number
  puissanceModuleWc?: number
  puissanceMaxKwc?: number
  clientEmail?: string
  assuranceAnnuelle?: number
  avecMaintenance: boolean
  coutRaccordement: number
  /** Coût de départ de la projection (coût net TTC + intérêts d'emprunt), comme à l'écran ; défaut coutNetTTC */
  coutInvestissementTotal?: number
  /** Coût de l'emprunt affiché à l'écran : intérêts payés (total des mensualités − montant emprunté) */
  coutEmprunt?: number
  avecFinancement: boolean
  apportPct: number
  mensualite: number
  tauxEmprunt: number
  dureeEmprunt: number
  economiesAn1: number
  rachatSurplus: number
  ecoAutoconsoDir: number
  ecoBat: number
  projectionBalance: number[]
  inflationElec: number
  avecAssurance: boolean
  amortRows: any[]
  monthlyData: any[]
  taxeMention: string   // '(TTC)' pour un particulier, '(HT)' sinon
}

/** Polices Poppins en base64 (TTF). Light et ExtraBold : facultatives (repli sur Regular / Bold). */
export interface PolicesRapport {
  light?: string
  regular: string
  medium: string
  semibold: string
  bold?: string
  extrabold?: string
}

/** Images (data URL) : dessins extraits du gabarit et graphiques rendus par Chart.js. Une image absente n'est pas affichée. */
export interface ImagesRapport {
  logoEpg?: string
  logosMarques?: string
  couverture?: string
  puce?: string
  fondPresentation?: string
  fondProjet?: string
  fondAgences?: string
  synoptique?: string
  jourConso?: string
  prodConso?: string
  donutProd?: string
  donutConso?: string
  repartition?: string
  flux?: string
  joursTypes?: string[]   // printemps, été, automne, hiver × semaine, week-end
  projection?: string
  donutEconomies?: string
  factures?: string
  reseau?: string
}

/** Largeur d'un texte en points (police Poppins de la graisse donnée) ; sert à réduire un texte trop long pour sa case. */
export type Mesure = (texte: string, graisse: number, taille: number) => number

/** Ancienne interface des pages financières (essai du 15/10/2026), gardée pour les tests */
export type DonneesPagesFinancieres = PdfReportData
export interface ImagesPagesFinancieres { projection: string; donutEconomies: string }

// ── Couleurs ──────────────────────────────────────────────────────────────
var VERT = '#91fcae'
var BLEU = '#1a74b8'
var CYAN = '#3aacc9'
var DEGRADE = 'linear-gradient(90deg, ' + VERT + ', ' + BLEU + ')'
var C_YEL = '#f5c518'
var C_ORA = '#ff9800'
var C_GRN = '#4caf50'
var C_BLU = '#1976d2'
var C_RED = '#e53935'
var C_GRY = '#90a4ae'

/** Tarif à heures super creuses (HP / HC / HSC, toute l'année ou par saison). */
export function avecHsc(d: { typeTarif: string }): boolean {
  return d.typeTarif === 'hsc' || d.typeTarif === 'hsc_saisonnier'
}

// ── Formats (mêmes textes que les champs du gabarit) ─────────────────────
function espaces(s: string): string {
  return s.replace(/[  ]/g, ' ')
}
export function echapper(s: any): string {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}
/** Montant en euros à la française, espaces ordinaires. */
export function eurHtml(v: number): string {
  return espaces(new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0))
}
export function kwhHtml(v: number): string {
  return espaces(new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(v || 0)) + ' kWh'
}
export function pctHtml(v: number): string {
  return dec(v, 1) + ' %'
}
/** Nombre à la française avec n décimales (virgule), comme à l'écran (16/10/2026 : le point du gabarit est abandonné). */
export function dec(v: any, n: number): string {
  return espaces(new Intl.NumberFormat('fr-FR', { minimumFractionDigits: n, maximumFractionDigits: n }).format(v || 0))
}
/** Puissance crête comme à l'écran : « 6,86 kWc ». */
export function kwcTexte(v: number): string {
  return dec(v, 2) + ' kWc'
}
/** Téléphone à 10 chiffres écrit par paires : « 06 01 01 01 01 » ; autre format laissé tel quel. */
export function telephoneTexte(t: any): string {
  var s = String(t == null ? '' : t).trim()
  var chiffres = s.replace(/[\s.\-]/g, '')
  return /^0\d{9}$/.test(chiffres) ? chiffres.replace(/(\d{2})(?=\d)/g, '$1 ') : s
}
/** Montant signé du tableau d'amortissement : « +1 234 € », « −1 234 € », « — » pour une case vide. */
export function eurSigne(v: number | null): string {
  if (v === null || v === undefined) return '—'
  var s = espaces(Math.round(Math.abs(v)).toLocaleString('fr-FR'))
  if (v < 0) return '−' + s + ' €'
  if (v > 0) return '+' + s + ' €'
  return s + ' €'
}
/** Mention « (HT) » / « (TTC) » ; vide si absente. */
export function mention(d: { taxeMention: string }): string {
  var m = (d.taxeMention || '').trim()
  if (!m) return ''
  return m.charAt(0) === '(' ? m : '(' + m + ')'
}
export function libelleSurplus(d: any): string {
  return d && d.zeroInjection ? 'Surplus non valorisé' : 'Surplus injecté'
}
function c2(v: any): string { return dec(v, 2) }

// ── Textes calculés (communs au rapport HTML et au gabarit de secours) ───
var PROFILS_PREDEFINIS: { [k: string]: string } = {
  maison_4pers: 'Maison 4 personnes', maison_2pers: 'Maison 2 personnes',
  appart_4pers: 'Appartement 4 personnes', appart_2pers: 'Appartement 2 personnes',
}

/** « Courbe de charge mesurée — HP 61 % · HC 39 % » ; profil prédéfini : « Profil type Maison 4 personnes ». */
export function profilConsommation(d: PdfReportData): string {
  var base = d.profileSource === 'predefined'
    ? 'Profil type ' + (PROFILS_PREDEFINIS[d.profilPredifini || ''] || d.profilPredifini || '')
    : 'Courbe de charge mesurée'
  var total = (d.consoHpKwh || 0) + (d.consoHcKwh || 0) + (d.consoHscKwh || 0)
  if (!d.hasTarifHC || total <= 0) return base.trim()
  var p = function (v: any) { return Math.round((v || 0) / total * 100) + ' %' }
  return base.trim() + ' — HP ' + p(d.consoHpKwh) + ' · HC ' + p(d.consoHcKwh) + (avecHsc(d) ? ' · HSC ' + p(d.consoHscKwh) : '')
}

/** « 36 kVA · triphasé » ; une étude ancienne peut porter « 36 kVA » (l'unité n'est écrite qu'une fois, B05). */
export function puissanceSouscrite(d: PdfReportData): string {
  var p = d.puissanceAbonnement ? String(d.puissanceAbonnement).replace(/\s*kVA\s*$/i, '') : ''
  return (p ? p + ' kVA' : '-') + (d.raccordement ? ' · ' + d.raccordement : '')
}

export function typeContrat(d: PdfReportData): string {
  return d.typeTarif === 'hsc_saisonnier' ? 'HP/HC/HSC Saisonnier'
    : d.typeTarif === 'hsc' ? 'HP/HC/HSC'
    : d.typeTarif === 'saisonnier' ? 'HP/HC Saisonnier' : d.hasTarifHC ? 'HP/HC' : 'Tarif unique'
}

/** Lignes de prix de l'électricité du tableau « Données d'entrée », selon le type de tarif : une ligne par période
 *  (HP, HC, HSC), prix d'hiver et d'été sur la même ligne pour un tarif saisonnier. Pas de plages horaires (19/10/2026 :
 *  plus de page d'annexe, seuls les prix sont utiles). */
export function lignesTarif(d: PdfReportData): { libelle: string; valeur: string }[] {
  var saisonnier = d.typeTarif === 'saisonnier' || d.typeTarif === 'hsc_saisonnier'
  var prix = function (annee: number, hiver: number, ete: number) {
    return saisonnier ? 'Hiver : ' + c2(hiver) + ' c€/kWh, Été : ' + c2(ete) + ' c€/kWh'
      : annee > 0 ? c2(annee) + ' c€/kWh' : '-'
  }
  var hp = prix(d.tarifHP, d.tarifHPHiver, d.tarifHPEte)
  if (d.typeTarif === 'unique' || !d.hasTarifHC) return [{ libelle: 'Tarif électricité', valeur: hp }]
  var lignes = [
    { libelle: 'Tarif électricité Heures Pleines', valeur: hp },
    { libelle: 'Tarif électricité Heures Creuses', valeur: prix(d.tarifHC, d.tarifHCHiver, d.tarifHCEte) },
  ]
  if (avecHsc(d)) lignes.push({ libelle: 'Tarif électricité Heures Super Creuses', valeur: prix(d.tarifHSC, d.tarifHSCHiver, d.tarifHSCEte) })
  return lignes
}

/** Coût moyen du kWh acheté = facture annuelle avant projet / consommation (c€/kWh). */
export function coutMoyenKwh(d: PdfReportData): number {
  return d.consoAnnuelle > 0 ? d.factureAvant / d.consoAnnuelle * 100 : 0
}

/** Coût du kWh solaire : valeur de l'écran ; repli : ancienne formule (investissement / 30 ans d'autoconsommation). */
export function coutKwhSolaire(d: PdfReportData): number {
  if (d.coutKwhSolaire != null) return d.coutKwhSolaire
  var auto = (d.autoconsoDir || 0) + (d.autoconsoBat || 0)
  return auto > 0 ? d.coutNetTTC / (auto * 30) * 100 : 0
}

/** « 6,86 kWc — 14 panneaux de 490 Wc » ; le maximum installable est sur sa propre ligne (puissanceMaxTexte). */
export function puissanceCentrale(d: PdfReportData): string {
  var n = (d.puissanceModuleWc || 0) > 0 ? Math.round(d.puissanceKwc * 1000 / (d.puissanceModuleWc || 1)) : 0
  return kwcTexte(d.puissanceKwc) + (n > 0 ? ' — ' + n + ' panneaux de ' + d.puissanceModuleWc + ' Wc' : '')
}
export function puissanceMaxTexte(d: PdfReportData): string {
  return (d.puissanceMaxKwc || 0) > 0 ? kwcTexte(d.puissanceMaxKwc || 0) : ''
}

/** Couverture : « Avec batterie de 14 kWh » / « Sans stockage ». */
export function puissanceBatterie(d: PdfReportData): string {
  return d.batteryKwh > 0 ? 'Avec batterie de ' + dec(d.batteryKwh, 0) + ' kWh' : 'Sans stockage'
}

/** Coût de l'emprunt = intérêts payés, comme à l'écran ; repli : total des mensualités − montant emprunté. */
export function coutEmprunt(d: PdfReportData): number {
  if (!d.avecFinancement || !(d.mensualite > 0)) return 0
  if (d.coutEmprunt != null) return d.coutEmprunt
  return d.mensualite * 12 * (d.dureeEmprunt || 0) - d.coutNetTTC * (1 - (d.apportPct || 0) / 100)
}

/**
 * Description du câble raccourcie pour tenir sur une ligne du bilan des frais (page 10) : « alu » pour l'aluminium
 * (« aluminium 4 × 35 mm², 40 m » → « alu 4 × 35 mm², 40 m »).
 */
export function cableCourt(libelle: string): string {
  return libelle.replace('aluminium', 'alu')
}

/** Lignes du bilan des frais : détail de l'investissement comme le bloc CAPEX de l'écran, puis l'exploitation. */
export function lignesBilanFrais(d: PdfReportData): { libelle: string; valeur: string; total?: boolean; filet?: boolean }[] {
  var lignes: { libelle: string; valeur: string; total?: boolean; filet?: boolean }[] = []
  var detail = d.coutInstallationPV != null
  // chiffrage au matériel (03/11/2026) : une ligne par section du CAPEX, détail en annexe ; câble : section et longueur
  if (d.sectionsCapex && d.sectionsCapex.length) {
    d.sectionsCapex.forEach(function (sec) { lignes.push({ libelle: sec.libelle, valeur: eurHtml(sec.montant) }) })
    if (d.cableLibelle) {
      lignes.push({ libelle: 'dont câble onduleur ↔ TGBT (' + cableCourt(d.cableLibelle) + ')', valeur: (d.coutCable || 0) > 0 ? eurHtml(d.coutCable || 0) : 'Non chiffré' })
    }
    if ((d.coutRaccordement || 0) > 0) lignes.push({ libelle: 'Raccordement ENEDIS', valeur: eurHtml(d.coutRaccordement) })
    lignes.push({ libelle: 'Total investissement (détail en annexe)', valeur: eurHtml(d.coutNetTTC), total: true })
    var opexM = (d.maintenanceAnnuelle || 0) + (d.assuranceAnnuelle || 0)
    lignes.push({ libelle: 'Coût annuel maintenance / assurance', valeur: opexM > 0 ? eurHtml(opexM) + '/an' : 'Non', filet: true })
    return lignes
  }
  if (detail) {
    var annexe = d.detailMateriel && d.detailMateriel.length ? ', détail en annexe' : ''
    lignes.push({ libelle: 'Installation PV' + (d.puissanceKwc > 0 ? ' (' + kwcTexte(d.puissanceKwc) + annexe + ')' : ''), valeur: eurHtml(d.coutInstallationPV || 0) })
    if ((d.coutInstallationBatterie || 0) > 0) {
      lignes.push({ libelle: 'Installation batterie' + (d.batteryKwh > 0 ? ' (' + Math.round(d.batteryKwh) + ' kWh)' : ''), valeur: eurHtml(d.coutInstallationBatterie || 0) })
    }
    // ordre du 20/10/2026, comme l'écran : PV, batterie, câble, datalogger (pro), raccordement, travaux
    if (d.cableLibelle) {
      lignes.push({ libelle: 'Câble onduleur ↔ TGBT (' + cableCourt(d.cableLibelle) + ')', valeur: (d.coutCable || 0) > 0 ? eurHtml(d.coutCable || 0) : 'Non chiffré' })
    }
    if ((d.coutDatalogger || 0) > 0) lignes.push({ libelle: 'Datalogger', valeur: eurHtml(d.coutDatalogger || 0) })
  }
  // pas de ligne quand le raccordement n'est pas chiffré (retour du 15/10/2026)
  if ((d.coutRaccordement || 0) > 0) lignes.push({ libelle: 'Raccordement ENEDIS', valeur: eurHtml(d.coutRaccordement) })
  if (detail && (d.coutTravaux || 0) > 0) {
    var nature = (d.travauxLibelle || '').trim()
    lignes.push({ libelle: 'Travaux supplémentaires' + (nature ? ' (' + nature + ')' : ''), valeur: eurHtml(d.coutTravaux || 0) })
  }
  lignes.push({ libelle: detail ? 'Total investissement' : 'Investissement', valeur: eurHtml(d.coutNetTTC), total: true })
  var opex = (d.maintenanceAnnuelle || 0) + (d.assuranceAnnuelle || 0)
  lignes.push({ libelle: 'Coût annuel maintenance / assurance', valeur: opex > 0 ? eurHtml(opex) + '/an' : 'Non', filet: true })
  return lignes
}

/**
 * Hauteurs (pt) du graphique de projection et du camembert des économies de la page 10, pour que la page soit remplie
 * comme dans le gabarit : la place laissée par les deux cadres (selon leur nombre de lignes) va au graphique.
 */
export function dimensionsPage10(d: PdfReportData): { projection: number; donut: number } {
  var lignes = lignesBilanFrais(d).length + lignesFinancement(d).length
  var donut = 120
  var projection = Math.max(150, Math.min(260, HAUTEUR_LIBRE_PAGE10 - lignes * HAUTEUR_LIGNE_PAGE10 - donut))
  return { projection: projection, donut: donut }
}
/** Page 10 : hauteur d'une ligne des cadres et place pour les lignes et les deux graphiques (titres, marges, total déduits). */
var HAUTEUR_LIGNE_PAGE10 = 21
var HAUTEUR_LIBRE_PAGE10 = 505

/**
 * Adresse sans la ville ni ce qui suit (code postal, département, région, pays) : l'adresse d'un repère placé sur la
 * carte est le libellé complet du géocodeur (« Gavray-sur-Sienne, Coutances, Manche, Normandie, …, France »), et la
 * ligne « code postal ville » est écrite juste en dessous. Vide si l'adresse commence par la ville.
 */
export function adresseSansVille(adresse: string, cpVille: string): string {
  var simple = function (t: string) {
    return t.split('').map(function (c) { return c.normalize('NFD').charAt(0) }).join('').toLowerCase().replace(/[\s'’-]+/g, ' ').trim()
  }
  var cp = ((cpVille || '').match(/\b\d{5}\b/) || [])[0] || ''
  var ville = simple((cpVille || '').replace(/\b\d{5}\b/, ''))
  var garde: string[] = []
  var segments = (adresse || '').split(',')
  for (var i = 0; i < segments.length; i++) {
    var seg = segments[i].trim()
    if (!seg) continue
    if (ville && simple(seg).indexOf(ville) === 0) break
    var ip = cp ? seg.search(new RegExp('(^|\\s)' + cp + '\\b')) : -1
    if (ip >= 0) { if (seg.slice(0, ip).trim()) garde.push(seg.slice(0, ip).trim()); break }
    garde.push(seg)
  }
  return garde.join(', ').replace(/[\s,;-]+$/, '')
}

/** Lignes « Financement » (le coût de l'emprunt = intérêts). */
export function lignesFinancement(d: PdfReportData): { libelle: string; valeur: string }[] {
  var apport = d.coutNetTTC * ((d.apportPct || 0) / 100)
  var interets = coutEmprunt(d)
  return [
    { libelle: 'Montant de l’apport', valeur: d.avecFinancement ? eurHtml(apport) + ' (' + dec(d.apportPct, 0) + ' % d’apport)' : eurHtml(d.coutNetTTC) + ' (comptant)' },
    { libelle: 'Mensualités de remboursement', valeur: d.avecFinancement ? eurHtml(d.mensualite) + '/mois' : '-' },
    { libelle: 'Taux du prêt bancaire', valeur: d.avecFinancement ? dec(d.tauxEmprunt, 2) + ' % sur ' + (d.dureeEmprunt || 0) + ' ans' : '-' },
    { libelle: 'Coût de l’emprunt (intérêts)', valeur: interets > 0 ? eurHtml(interets) : '-' },
  ]
}

/** Colonnes du tableau d'amortissement, comme à l'écran (Step6Rapport.tableauAmortissement). */
export function colonnesAmortissement(d: PdfReportData): { cle: string; titre: string }[] {
  var cols = [
    { cle: 'cashAvant', titre: 'Coût élec. avant' },
    { cle: 'cashApres', titre: 'Coût élec. après' },
    { cle: 'economie', titre: 'Économies / vente' },
  ]
  if (d.avecFinancement) cols.push({ cle: 'emprunt', titre: 'Emprunt' })
  if (d.avecMaintenance) cols.push({ cle: 'maintenance', titre: 'Maintenance' })
  if (d.avecAssurance) cols.push({ cle: 'assurance', titre: 'Assurance' })
  cols.push({ cle: 'resultat', titre: 'Résultat d’exploit.' })
  cols.push({ cle: 'balance', titre: 'Balance cumulée' })
  return cols
}

/** Lignes du « Bilan annuel estimé » (page 12) : celles qui ne concernent pas l'étude sont omises, comme à l'écran. */
export function lignesBilanAnnuel(d: PdfReportData): { libelle: string; valeur: string; niveau: 'ligne' | 'sous' | 'groupe' | 'total' }[] {
  var l: { libelle: string; valeur: string; niveau: 'ligne' | 'sous' | 'groupe' | 'total' }[] = [
    { libelle: 'Facture avant', valeur: eurHtml(d.factureAvant), niveau: 'ligne' },
    { libelle: 'Facture après', valeur: eurHtml(d.factureApres), niveau: 'ligne' },
    { libelle: 'Économie de facture', valeur: eurHtml(d.factureAvant - d.factureApres), niveau: 'groupe' },
    { libelle: 'Autoconsommation directe', valeur: eurHtml(d.ecoAutoconsoDir), niveau: 'sous' },
  ]
  if (d.hasBattery) l.push({ libelle: 'Via batterie', valeur: eurHtml(d.ecoBat), niveau: 'sous' })
  if (!d.zeroInjection) l.push({ libelle: 'Vente de surplus', valeur: eurHtml(d.rachatSurplus), niveau: 'groupe' })
  l.push({ libelle: 'Économie totale', valeur: eurHtml(d.economiesAn1), niveau: 'total' })
  return l
}

// ── Briques de mise en page ───────────────────────────────────────────────
var LARGEUR_PAGE = 595.28
var HAUTEUR_PAGE = 841.89
var TAILLE_MIN = 8

/** Largeur approchée (Poppins ≈ 0,58 em par caractère) quand aucune mesure exacte n'est fournie. */
function mesureApprochee(texte: string, graisse: number, taille: number): number {
  return texte.length * taille * (graisse >= 600 ? 0.62 : 0.58)
}

/**
 * Taille de police pour qu'un texte tienne sur une ligne de la largeur donnée : réduite par demi-point, jamais sous 8 pt
 * (même règle que les cases du gabarit, B06).
 */
export function tailleQuiTient(texte: string, largeur: number, taille: number, graisse: number, mesure?: Mesure, minimum?: number): number {
  var m = mesure || mesureApprochee
  var min = minimum || TAILLE_MIN
  while (taille > min && m(texte, graisse, taille) > largeur) taille -= 0.5
  return taille
}

interface OptionsTexte {
  taille?: number
  graisse?: number
  couleur?: string
  centre?: boolean      // x = centre du texte
  droite?: boolean      // x = bord droit du texte
  italique?: boolean
  classe?: string
  balise?: string       // h1 / h2 : titres repris dans les signets du PDF
}

/** Texte sur une ligne dont la ligne de base est à `base` (points depuis le haut de la page). */
function texte(t: string, x: number, base: number, o?: OptionsTexte): string {
  o = o || {}
  var taille = o.taille || 12
  var style = 'top:' + r(base - 0.85 * taille) + 'pt;font-size:' + taille + 'pt'
    + (o.graisse && o.graisse !== 400 ? ';font-weight:' + o.graisse : '')
    + (o.couleur ? ';color:' + o.couleur : '')
    + (o.italique ? ';font-style:italic' : '')
  if (o.centre) style += ';left:' + r(x - 200) + 'pt;width:400pt;text-align:center'
  else if (o.droite) style += ';right:' + r(LARGEUR_PAGE - x) + 'pt;text-align:right'
  else style += ';left:' + r(x) + 'pt'
  var balise = o.balise || 'div'
  return '<' + balise + ' class="a' + (o.classe ? ' ' + o.classe : '') + '" style="' + style + '">' + t + '</' + balise + '>'
}

/** Valeur dans une case du gabarit (x, haut, largeur, hauteur), centrée verticalement, police réduite si trop longue. */
function caseValeur(t: string, x: number, haut: number, largeur: number, hauteur: number, mesure?: Mesure, o?: OptionsTexte): string {
  o = o || {}
  var taille = tailleQuiTient(t, largeur - 4, o.taille || 12, o.graisse || 400, mesure, o.taille && o.taille < TAILLE_MIN ? o.taille : undefined)
  return texte(echapper(t), x + 2, haut + hauteur / 2 + 0.35 * taille, { taille: taille, graisse: o.graisse, couleur: o.couleur, classe: o.classe })
}

function r(v: number): string { return String(Math.round(v * 100) / 100) }

function rect(x: number, haut: number, l: number, h: number, style: string): string {
  return '<div class="a" style="left:' + r(x) + 'pt;top:' + r(haut) + 'pt;width:' + r(l) + 'pt;height:' + r(h) + 'pt;' + style + '"></div>'
}
function cadre(x: number, haut: number, l: number, h: number): string {
  return '<div class="a cadre-abs" style="left:' + r(x) + 'pt;top:' + r(haut) + 'pt;width:' + r(l) + 'pt;height:' + r(h) + 'pt"></div>'
}
function separateur(x: number, haut: number, h: number): string {
  return rect(x, haut, 1.4, h, 'background:' + BLEU)
}
function image(src: string | undefined, x: number, haut: number, l: number, h: number, classe?: string): string {
  if (!src) return ''
  return '<img class="a' + (classe ? ' ' + classe : '') + '" style="left:' + r(x) + 'pt;top:' + r(haut) + 'pt;width:' + r(l) + 'pt;height:' + r(h) + 'pt" src="' + src + '" alt="">'
}
/** Pastille de légende : rond plein, ou carré arrondi évidé (anneau) comme dans le gabarit. */
function pastille(x: number, base: number, couleur: string, anneau?: boolean, taille?: number): string {
  var t = taille || 9
  var haut = base - t * 0.82
  return rect(x, haut, t, t, anneau
    ? 'border:1.6pt solid ' + couleur + ';border-radius:2.5pt;background:#fff'
    : 'border-radius:50%;background:' + couleur)
}

/** Titre de section (« 2. PRÉSENTATION DU SITE ») et son filet ; sans filet si le fond du gabarit le dessine déjà. */
function titreSection(t: string, filet: boolean): string {
  return texte(echapper(t), 28.3, 43.0, { taille: 14, graisse: 600, balise: 'h1' })
    + (filet ? rect(28.3, 51.2, 538.6, 1.7, 'background:' + DEGRADE) : '')
}
/** Sous-titre (Poppins Medium 12) avec la mention HT / TTC éventuelle. */
function sousTitre(t: string, x: number, base: number, m?: string): string {
  return texte(echapper(t) + (m ? '<span class="mention">' + echapper(m) + '</span>' : ''), x, base, { taille: 12, graisse: 500, balise: 'h2' })
}

function piedDePage(numero: number, filet: boolean): string {
  return texte('Émeraude Power Group', LARGEUR_PAGE / 2, 798.5, { taille: 10, graisse: 600, centre: true })
    + (filet ? rect(14.2, 806.3, 566.9, 1.3, 'background:' + DEGRADE) : '')
    + texte('2 rue du Mabilais 35000 Rennes - SIRET : 94046086800018', LARGEUR_PAGE / 2, 827.7, { taille: 10, graisse: 300, centre: true })
    + texte(String(numero), 581.1, 827.7, { taille: 10, couleur: CYAN, droite: true })
}

function page(id: string, contenu: string, classe?: string): string {
  return '<section class="page' + (classe ? ' ' + classe : '') + '" id="' + id + '">' + contenu + '</section>'
}

// ── Pages ─────────────────────────────────────────────────────────────────

/** Page 1 — couverture. */
export function pageCouverture(d: PdfReportData, im: ImagesRapport, mesure?: Mesure): string {
  var champ = function (t: string, x: number, haut: number) { return caseValeur(t || '', x, haut, 232.8, 22.5, mesure) }
  return page('page-1',
    image(im.couverture, 0, 335, LARGEUR_PAGE, 506.89)
    + image(im.logoEpg, 128, 45, 336, 108)
    + image(im.logosMarques, 165, 155, 268, 46)
    + '<div class="a titre-couverture" style="left:28.7pt;top:' + r(277.8 - 0.85 * 28) + 'pt">'
    + '<span style="font-size:28pt;font-weight:500">VOTRE PROJET</span><br>'
    + '<span style="font-size:29pt;font-weight:800;line-height:1.22">PHOTOVOLTAÏQUE</span></div>'
    + rect(326.2, 245, 2.2, 86, 'background:' + BLEU)
    + champ(d.projectName, 348.3, 243.8)
    + champ(kwcTexte(d.puissanceKwc), 348.3, 276.7)
    + champ(d.batteryKwh > 0 ? puissanceBatterie(d) : '', 348.3, 309.7)
    + champ(d.commercial, 14.2, 711.7)
    + champ(d.agence, 14.2, 742.9)
    + champ(telephoneTexte(d.telephone), 14.2, 774.0)
    + champ(d.commercialEmail, 14.2, 805.2), 'couverture')
}

/** Entrées du sommaire (titre, sous-titres, page). */
export function entreesSommaire(d: PdfReportData): { titre: string; page: number; sous: { titre: string; page: number }[] }[] {
  var e = [
    { titre: '1. PROJET', page: 4, sous: [] as { titre: string; page: number }[] },
    { titre: '2. PRÉSENTATION DU SITE', page: 5, sous: [
      { titre: 'Données d’entrée', page: 5 }, { titre: 'Aperçu d’une journée de consommation standard', page: 5 },
      { titre: 'Caractéristiques de l’installation photovoltaïque', page: 6 }, { titre: 'Synoptique simplifié d’une installation photovoltaïque', page: 6 }] },
    { titre: '3. ANALYSE ÉNERGÉTIQUE', page: 7, sous: [
      { titre: 'Production vs. Consommation annuelle', page: 7 }, { titre: 'Production annuelle', page: 7 },
      { titre: 'Répartition de la production mensuelle', page: 7 }, { titre: 'Consommation annuelle', page: 7 },
      { titre: 'Flux énergétique', page: 8 }, { titre: 'Journées types par saison', page: 9 }] },
    { titre: '4. ANALYSE FINANCIÈRE', page: 10, sous: [
      { titre: 'Bilan des frais', page: 10 }, { titre: 'Financement', page: 10 }, { titre: 'Projection financière sur 30 ans en €', page: 10 },
      { titre: 'Détail des économies (an 1)', page: 10 }, { titre: 'Détail de l’amortissement annuel', page: 11 }] },
    { titre: '5. IMPACT SUR LES FACTURES', page: 12, sous: [
      { titre: 'Bilan annuel estimé', page: 12 }, { titre: 'Factures mensuelles (€)', page: 12 }, { titre: 'Demande sur le réseau (kWh)', page: 12 }] },
    { titre: '6. CONTRATS DE RACCORDEMENT AU RÉSEAU ET D’ACHAT', page: 13, sous: [] },
    { titre: '7. LIMITES DE PRESTATIONS', page: 13, sous: [] },
    { titre: 'NOS AGENCES EN FRANCE', page: 14, sous: [] },
  ]
  return e
}

/** Page 2 — sommaire, avec liens vers les pages. */
export function pageSommaire(d: PdfReportData): string {
  var lignes = entreesSommaire(d).map(function (e, i) {
    return '<a class="som-titre' + (i === 0 ? ' premier' : '') + '" href="#page-' + e.page + '"><span>' + echapper(e.titre) + '</span><span>' + e.page + '</span></a>'
      + e.sous.map(function (s) {
        return '<a class="som-sous" href="#page-' + s.page + '"><span class="lib">' + echapper(s.titre) + '</span><span class="points"></span><span>' + s.page + '</span></a>'
      }).join('')
  }).join('')
  return page('page-2', titreSection('SOMMAIRE', true) + '<nav class="sommaire">' + lignes + '</nav>')
}

/** Page 3 — présentation du groupe (dessins du gabarit, textes ici). */
export function pagePresentation(im: ImagesRapport): string {
  var bleu = '#1977ba', vert = '#9ccd9e', bleu2 = '#1f71ae'
  var t = function (s: string, x: number, base: number, taille: number, graisse: number, couleur: string) {
    return texte(s, x, base, { taille: taille, graisse: graisse, couleur: couleur })
  }
  var centre = function (s: string, cx: number, base: number, taille: number, graisse: number, couleur: string) {
    return texte(s, cx, base, { taille: taille, graisse: graisse, couleur: couleur, centre: true })
  }
  return page('page-3', image(im.fondPresentation, 0, 0, LARGEUR_PAGE, HAUTEUR_PAGE)
    + centre('INTÉGRATEUR DE SOLUTIONS ÉNERGÉTIQUES', 297.3, 142.3, 12, 700, '#fff')
    + centre('19', 116.85, 236.9, 30.6, 700, vert) + centre('ans', 116.85, 254.2, 14.4, 400, bleu)
    + centre('d’expérience', 116.85, 271.5, 14.4, 400, bleu)
    + t('Expert du', 231, 214.3, 14.4, 400, bleu) + t('photovoltaïque', 231, 231.6, 14.4, 600, vert)
    + t('agricole', 231, 249, 14.4, 600, vert) + t('en France', 231, 266.3, 14.4, 400, bleu)
    + centre('10 000', 82.1, 387.3, 25, 600, vert) + centre('installations', 82.2, 411.9, 14.4, 400, bleu) + centre('depuis 2007', 82.2, 429.2, 14.4, 400, bleu)
    + centre('860', 209.6, 387.3, 25, 600, vert) + centre('installations', 209.6, 411.9, 14.4, 400, bleu) + centre('en 2025', 209.6, 429.2, 14.4, 400, bleu)
    + centre('158 MWc', 350.9, 387.3, 25, 600, vert) + centre('installés', 351, 411.9, 14.4, 400, bleu) + centre('en 2025', 351, 429.2, 14.4, 400, bleu)
    + t('6 SOLUTIONS', 58.2, 548.9, 19.5, 700, bleu)
    + ['Autoconsommation individuelle', 'Autoconsommation collective', 'Vente de surplus', 'Vente totale', 'Stockage', 'Repowering']
      .map(function (s, i) { return t(s, 74.5, 580.5 + i * 23.64, 13, 400, bleu) }).join('')
    + t('5 SECTEURS', 382.9, 520.8, 19.5, 700, bleu2)
    + [['Agricole', 557.8], ['Industriel', 598.4], ['Tertiaire', 637.8], ['Collectivités', 680.2], ['Particuliers', 719.8]]
      .map(function (s: any) { return t(s[0], 433.8, s[1], 13, 400, bleu2) }).join(''))
}

var AGENCES = [
  { nom: 'Émeraude Solaire - Nord-Ouest', lignes: ['15 rue Saint Coulban', '35540 Miniac-Morvan', '02 99 81 78 50', 'contact@emeraude-solaire.com'] },
  { nom: 'Émeraude Solaire - Sud-Ouest', lignes: ['8-10 rue Cantelaudette', '33100 Lormont', '05 56 81 06 67', 'contact@emeraude-solaire.com'] },
  { nom: 'Systèmes Solaires', lignes: ['20 rue le Corbusier', '63800 Cournon d’Auvergne', '04 73 62 61 82', 'contact@systemessolaires.fr'] },
]

/** Page 4 — « 1. PROJET » : client, commercial, date, adresse du site. */
export function pageProjet(d: PdfReportData, im: ImagesRapport, mesure?: Mesure): string {
  var v = function (t: string, haut: number) { return caseValeur(t || '', 272.1, haut, 254.8, 19.5, mesure) }
  var rue = adresseSansVille(d.clientAddress, d.cpVille)
  var contact = [telephoneTexte(d.clientTelephone), d.clientEmail].filter(function (x) { return x && String(x).trim() }).join(' · ')
  var pied = texte('Émeraude Power Group', LARGEUR_PAGE / 2, 748.3, { taille: 12, graisse: 600, centre: true })
    + AGENCES.map(function (a, i) {
      var x = [14.2, 226.2, 438.3][i]
      return texte(echapper(a.nom), x, 779.7, { taille: 10, graisse: 500 })
        + a.lignes.map(function (l, j) { return texte(echapper(l), x, 791.7 + j * 12, { taille: 10, graisse: 300 }) }).join('')
    }).join('')
  return page('page-4', image(im.fondProjet, 0, 0, LARGEUR_PAGE, HAUTEUR_PAGE)
    + titreSection('1. PROJET', false)
    + texte('CLIENT', 78.4, 210.0, { taille: 14, graisse: 600 })
    + texte('COMMERCIAL', 78.4, 346.5, { taille: 14, graisse: 600 })
    + texte('DATE DE L’ÉTUDE', 78.4, 386.4, { taille: 14, graisse: 600 })
    + [d.raisonSociale || d.clientName, d.clientName, rue, d.cpVille, contact].filter(function (x) { return x && String(x).trim() })
      .map(function (x, i) { return v(x, [194.9, 220.7, 246.3, 271.9, 297.5][i]) }).join('')
    + v(d.commercial, 331.4) + v(d.dateEtude, 371.3)
    + texte('ADRESSE DU SITE DE PRODUCTION', 70.9, 539.9, { taille: 14, graisse: 600 })
    + [rue, d.cpVille].filter(function (x) { return x && String(x).trim() })
      .map(function (x, i) { return caseValeur(x, 70.9, [561.6, 594.9][i], 476.7, 19.5, mesure) }).join('')
    + pied)
}

/** Page 5 — « 2. PRÉSENTATION DU SITE » : profil, journée standard, données d'entrée (selon le type de tarif). */
export function pageSite(d: PdfReportData, im: ImagesRapport, mesure?: Mesure): string {
  var m = mention(d)
  var avertissements = d.avertissementsExtrapolation || []
  var hauteurGraphique = avertissements.length ? 158 : 201.2
  var legende = legendeEnLigne([{ texte: 'Hiver - Semaine', couleur: '#1565c0', anneau: true }, { texte: 'Hiver - Week-end', couleur: '#90caf9', anneau: true },
    { texte: 'Été - Semaine', couleur: '#e65100', anneau: true }, { texte: 'Été - Week-end', couleur: '#ffcc80', anneau: true }], 29, 168.7, 537.9, 11, 'entre')

  var alerte = ''
  if (avertissements.length) {
    var fort = avertissements.some(function (a) { return a.fort })
    alerte = '<div class="a alerte' + (fort ? ' forte' : '') + '" style="left:28.3pt;top:' + r(184 + hauteurGraphique + 6) + 'pt;width:538.6pt;height:44pt">'
      + echapper(avertissements.map(function (a) { return a.texte }).join(' ')) + '</div>'
  }

  var lignes: { libelle: string; valeur: string; mention?: boolean; sous?: string }[] = [
    { libelle: 'Consommation annuelle', valeur: kwhHtml(d.consoAnnuelle) },
    { libelle: 'Puissance souscrite', valeur: puissanceSouscrite(d) },
    { libelle: 'Coût énergétique annuel estimé', valeur: eurHtml(d.factureAvant) + '/an', mention: true, sous: '(avant projet)' },
    { libelle: 'Type de contrat', valeur: typeContrat(d) },
  ]
  lignesTarif(d).forEach(function (l) { lignes.push({ libelle: l.libelle, valeur: l.valeur, mention: true }) })
  // tarif : deux décimales comme les prix du kWh (19/10/2026 ; arrondi à l'euro auparavant)
  lignes.push({ libelle: 'Tarif abonnement annuel', valeur: dec(d.tarifAbonnement, 2) + ' €/an', mention: true })
  lignes.push({ libelle: 'Taxes acheminement / accises', valeur: d.taxeelec > 0 ? c2(d.taxeelec) + ' c€/kWh' : '-', mention: d.taxeelec > 0 })
  lignes.push({ libelle: 'Coût moyen du kWh', valeur: c2(coutMoyenKwh(d)) + ' c€/kWh', mention: true })
  var pas = Math.min(34.2, 300 / lignes.length)
  var tableau = lignes.map(function (l, i) {
    var base = 444.9 + i * pas
    // libellé réduit s'il déborde sur le séparateur (« Tarif électricité Heures Super Creuses (TTC) », 19/10/2026)
    var avecMention = l.mention && m
    var taille = tailleQuiTient(l.libelle + (avecMention ? '  ' + m : ''), 304.6 - 46 - 8, 12, 400, mesure)
    return texte(echapper(l.libelle) + (avecMention ? '<span class="mention">' + echapper(m) + '</span>' : ''), 46, base, { taille: taille })
      + (l.sous ? texte(echapper(l.sous), 46, base + (pas < 34 ? 12 : 14), { taille: pas < 34 ? 9 : 10 }) : '')
      + caseValeur(l.valeur, 326, base - 12.5, 223.9, 16.4, mesure)
  }).join('')

  return page('page-5', titreSection('2. PRÉSENTATION DU SITE', true)
    + sousTitre('Données d’entrée', 28.3, 79.2)
    + texte('Profil de consommation :', 28.3, 100.8, { taille: 12 })
    + caseValeur(profilConsommation(d), 182.6, 85.8, 384.4, 21.4, mesure)
    + sousTitre('Aperçu d’une journée de consommation standard', 28.3, 143.0)
    + legende
    + image(im.jourConso, 28.3, 184, 538.6, hauteurGraphique)
    + alerte
    + cadre(28.9, 401, 537.7, 361) + separateur(304.6, 421, 317)
    + tableau
    + piedDePage(5, true))
}

/** Page 6 — caractéristiques de l'installation, cycles de batterie, synoptique. */
export function pageInstallation(d: PdfReportData, im: ImagesRapport, mesure?: Mesure): string {
  var m = mention(d)
  var cout = coutKwhSolaire(d)
  var lignes: [string, string, boolean][] = [['Puissance de la centrale en kWc', puissanceCentrale(d), false]]
  // maximum installable : information secondaire, en petit sous la puissance retenue (retour du 16/10/2026)
  var max = puissanceMaxTexte(d)
  lignes = lignes.concat([
    ['Puissance de la batterie en kWh', d.batteryKwh > 0 ? dec(d.batteryKwh, 0) + ' kWh' : 'Sans stockage', false],
    ['Nombre de cycles par an', d.batteryKwh > 0 ? d.cyclesBatterieAn + ' cycles/an' : '-', false],
    ['Coût du kWh solaire', cout > 0 ? c2(cout) + ' c€/kWh' : '-', cout > 0],
    ['Productible', (d.puissanceKwc > 0 ? Math.round(d.productionAnnuelle / d.puissanceKwc) : 0) + ' kWh/kWc/an', false],
    ['Production totale annuelle', kwhHtml(d.productionAnnuelle), false],
  ] as [string, string, boolean][])
  // avec la ligne du maximum, les lignes suivantes descendent de 10 pt et se resserrent pour rester dans le cadre
  var pasLigne = max ? 21 : 22.78
  var bloc = lignes.map(function (l, i) {
    var base = 84.5 + i * pasLigne + (max && i > 0 ? 10 : 0)
    return texte(echapper(l[0]) + (l[2] && m ? '<span class="mention">' + echapper(m) + '</span>' : ''), 46, base, { taille: 12 })
      + caseValeur(l[1], 278.9, base - 12.5, 274.8, 16.4, mesure)
      + (i === 0 && max ? texte('max. installable : ' + echapper(max), 280.9, base + 12, { taille: 9, graisse: 300, couleur: '#666' }) : '')
  }).join('')
  var colonnes = [75.3, 155.2, 264.5, 373.7, 483]
  var cycles = [['C / J', 'C / an', '6 000', '9 000', '10 000'], ['0,5', '183', '33', '49', '55'], ['0,75', '274', '22', '33', '37'],
    ['1', '365', '16', '25', '27'], ['1,25', '456', '13', '20', '22'], ['1,5', '548', '11', '16', '18'], ['2', '730', '8', '12', '14']]
  // ligne du tableau la plus proche du nombre de cycles de l'étude, surlignée
  var ligneEtude = -1
  if (d.batteryKwh > 0 && d.cyclesBatterieAn > 0) {
    var ecart = Infinity
    cycles.slice(1).forEach(function (c, i) {
      var e = Math.abs(parseInt(c[1], 10) - d.cyclesBatterieAn)
      if (e < ecart) { ecart = e; ligneEtude = i + 1 }
    })
  }
  var surlignage = ligneEtude > 0 ? rect(36, 261.9 + ligneEtude * 22.35 - 15.5, 522, 21, 'background:#e8f5e9;border-radius:4pt') : ''
  var tableCycles = cycles.map(function (ligne, i) {
    return ligne.map(function (v, j) {
      return texte(v, colonnes[j], 261.9 + i * 22.35, { taille: 12, graisse: i === 0 ? 600 : 400, centre: true })
    }).join('')
  }).join('')
  return page('page-6', sousTitre('Caractéristiques de l’installation photovoltaïque', 28.3, 40.9)
    + cadre(28.9, 55, 537.7, 159.6) + separateur(265, 66, 137)
    + bloc
    + cadre(28.9, 234, 537.7, 178) + separateur(114.5, 243, 158) + separateur(193.5, 243, 158)
    + surlignage
    + texte('Durée de vie de la batterie (années) selon les cycles garantis', 373.7, 246.5, { taille: 9, graisse: 500, centre: true, couleur: '#555' })
    + tableCycles
    + '<div class="a" style="left:28.3pt;width:538.6pt;top:419pt;font-size:9pt;font-weight:300;font-style:italic;text-align:center;line-height:1.35;white-space:normal">'
    + 'C / J : cycles par jour · C / an : cycles par an · 6 000, 9 000, 10 000 : cycles garantis par le fabricant'
    + (ligneEtude > 0 ? '<br>En vert : la ligne la plus proche de la batterie de l’étude (' + d.cyclesBatterieAn + ' cycles par an)' : '') + '</div>'
    + sousTitre('Synoptique simplifié d’une installation photovoltaïque', 28.3, 464.2)
    + image(im.synoptique, 0, 466, LARGEUR_PAGE, 312)
    + piedDePage(6, true))
}

/**
 * Légende d'un graphique à pastilles, sur une ligne : répartie sur toute la largeur (« entre »), ou serrée à gauche ou à
 * droite de la zone. Mise en page par le navigateur : aucun texte ne déborde, quelle que soit sa longueur.
 */
function legendeEnLigne(elements: { texte: string; couleur: string; anneau?: boolean; pointille?: boolean }[], x: number, base: number, largeur: number, taille: number,
  alignement: 'entre' | 'gauche' | 'droite', ecart?: number): string {
  var items = elements.map(function (e) {
    if (e.pointille) return '<span class="item"><span class="trait-legende" style="border-top-color:' + e.couleur + '"></span>' + echapper(e.texte) + '</span>'
    return '<span class="item"><span class="pastille-legende' + (e.anneau ? ' anneau' : '') + '" style="' + (e.anneau ? 'border-color:' : 'background:') + e.couleur + '"></span>'
      + echapper(e.texte) + '</span>'
  }).join('')
  var justification = alignement === 'entre' ? 'space-between' : alignement === 'droite' ? 'flex-end' : 'flex-start'
  if (ecart == null) ecart = alignement === 'entre' ? 8 : 30
  return '<div class="a legende-ligne" style="left:' + r(x) + 'pt;width:' + r(largeur) + 'pt;top:' + r(base - 0.85 * taille) + 'pt;font-size:' + taille
    + 'pt;justify-content:' + justification + ';gap:' + ecart + 'pt">' + items + '</div>'
}

/** Légende verticale d'un camembert : libellé puis valeur, centrée sur le camembert. */
function legendeCamembert(elements: { texte: string; valeur: string; couleur: string }[], x: number, centre: number): string {
  var pas = 29.5
  var haut0 = centre - elements.length * pas / 2
  return elements.map(function (e, i) {
    var base = haut0 + i * pas + 8
    return pastille(x - 14, base, e.couleur) + texte(echapper(e.texte), x, base, { taille: 10, graisse: 300 })
      + texte(echapper(e.valeur), x, base + 13.4, { taille: 9 })
  }).join('')
}

/** Page 7 — « 3. ANALYSE ÉNERGÉTIQUE ». */
export function pageEnergetique(d: PdfReportData, im: ImagesRapport, mesure?: Mesure): string {
  var bat = d.hasBattery && d.autoconsoBat > 0
  // kWh et part en %, comme les légendes de l'écran (Step6Rapport : totalProdRef, totalConsoRef)
  var totalProd = Math.max((d.autoconsoDir || 0) + (bat ? (d.autoconsoBat || 0) + (d.pertesBat || 0) : 0) + (d.surplus || 0), 1)
  var totalConso = Math.max((d.autoconsoDir || 0) + (bat ? d.autoconsoBat || 0 : 0) + (d.reseau || 0), 1)
  var part = function (v: number, total: number) { return kwhHtml(v) + ' (' + pctHtml((v || 0) / total * 100) + ')' }
  var prod = [{ texte: 'Autoconsommé direct', valeur: part(d.autoconsoDir, totalProd), couleur: C_YEL }]
  if (bat) prod.push({ texte: 'Via batterie', valeur: part(d.autoconsoBat, totalProd), couleur: C_ORA })
  prod.push({ texte: libelleSurplus(d), valeur: part(d.surplus, totalProd), couleur: C_GRN })
  if (bat && d.pertesBat > 0) prod.push({ texte: 'Pertes batterie', valeur: part(d.pertesBat, totalProd), couleur: C_RED })
  var conso = [{ texte: 'Couverte par le solaire', valeur: part(d.autoconsoDir, totalConso), couleur: C_YEL }]
  if (bat) conso.push({ texte: 'Via batterie', valeur: part(d.autoconsoBat, totalConso), couleur: C_ORA })
  conso.push({ texte: 'Couverte par le réseau', valeur: part(d.reseau, totalConso), couleur: C_BLU })
  var repart = [{ texte: 'Autoconso. directe (kWh)', couleur: C_YEL }]
  if (bat) repart.push({ texte: 'Via batterie (kWh)', couleur: C_ORA })
  repart.push({ texte: libelleSurplus(d) + ' (kWh)', couleur: C_GRN })
  return page('page-7', titreSection('3. ANALYSE ÉNERGÉTIQUE', true)
    + sousTitre('Production vs. Consommation annuelle', 28.3, 83.5)
    + legendeEnLigne([{ texte: 'Production (kWh)', couleur: C_YEL }, { texte: 'Consommation (kWh)', couleur: C_BLU }], 29, 106.3, 537.9, 11, 'gauche', 40)
    + image(im.prodConso, 28.3, 117.6, 538.6, 201.2)
    + sousTitre('Production annuelle', 28.3, 341.3) + sousTitre('Consommation annuelle', 312.7, 341.3)
    + texte('Taux d’autoconsommation :', 28.3, 362.5, { taille: 12 }) + caseValeur(pctHtml(d.tauxAutoconso), 199, 350.8, 98.6, 14.7, mesure)
    + texte('Taux d’autoproduction :', 312.7, 362.5, { taille: 12 }) + caseValeur(pctHtml(d.tauxAutoprod), 458.6, 350.8, 98.6, 14.7, mesure)
    + image(im.donutProd, 28.3, 389.1, 109.1, 109.1) + image(im.donutConso, 312.7, 389.1, 109.1, 109.1)
    + legendeCamembert(prod, 163, 443.6) + legendeCamembert(conso, 446.8, 443.6)
    + sousTitre('Répartition de la production mensuelle', 28.3, 532.7)
    + legendeEnLigne(repart, 31, 555.4, 535.9, 10, 'gauche', 34)
    + image(im.repartition, 28.3, 569.9, 538.6, 201.2)
    + piedDePage(7, true))
}

/** Page 8 — flux énergétique. */
export function pageFlux(im: ImagesRapport): string {
  // 16/10/2026 : étiquettes plus grandes ; hauteur modérée pour ne pas déformer le dessin (1 078 × 728 px à l'origine)
  return page('page-8', sousTitre('Flux énergétique', 28.3, 40.9)
    + '<div class="a" style="left:28.3pt;top:52pt;width:538.6pt;font-size:10pt;font-weight:300;color:#555;white-space:normal;line-height:1.35">'
    + 'D’où vient l’énergie consommée et où va l’énergie produite sur une année (kWh). L’épaisseur de chaque flux est proportionnelle à l’énergie.</div>'
    + image(im.flux, 28.3, 84, 538.6, HAUTEUR_FLUX) + piedDePage(8, true))
}
/** Hauteur (pt) du flux énergétique de la page 8 ; l'image est dessinée à 2 px par point. */
export var HAUTEUR_FLUX = 400

/** Hauteur (pt) des 8 graphiques de la page 9 (98,7 pt avant le 16/10/2026) ; images dessinées à 2 px par point. */
export var HAUTEUR_JOUR_TYPE = 140

var CLES_SAISONS = ['printemps', 'ete', 'automne', 'hiver']
var MOIS_COURTS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.']

/** Journée type d'une saison (03/11/2026), ou null pour un calcul plus ancien. */
export function journeeType(d: PdfReportData, saison: string, weekEnd: boolean, critere: string): any {
  return (d.journeesTypes || []).find(function (j) { return j.saison === saison && j.weekEnd === weekEnd && j.critere === critere }) || null
}

/** « 5 juin » (aaaa-mm-jj). */
export function dateCourte(iso: string): string {
  var p = (iso || '').split('-')
  return p.length === 3 ? parseInt(p[2], 10) + ' ' + MOIS_COURTS[parseInt(p[1], 10) - 1] : ''
}

/** Page 9 — journées types par saison (semaine et week-end). */
export function pageJoursTypes(d: PdfReportData, im: ImagesRapport, mesure?: Mesure): string {
  // unités sur les axes (kW à gauche, % de la capacité de la batterie à droite) ; stock en pointillé comme sa courbe
  var legende: { texte: string; couleur: string; anneau?: boolean; pointille?: boolean }[] = [{ texte: 'Production', couleur: C_YEL, anneau: true },
    { texte: 'Consommation', couleur: C_BLU, anneau: true }, { texte: 'Soutirage réseau', couleur: C_RED, anneau: true }]
  if (d.hasBattery) legende.push({ texte: 'Stock batterie', couleur: C_ORA, pointille: true })
  var saisons = ['Printemps', 'Été', 'Automne', 'Hiver']
  var images = im.joursTypes || []
  var blocs = saisons.map(function (s, i) {
    var haut = 122 + i * 168
    return [0, 1].map(function (j) {
      var x = j === 0 ? 28.3 : 302.8
      var jt = journeeType(d, CLES_SAISONS[i], j === 1, 'conso+prod')
      return texte('Journée type ' + s + ' - ' + (j === 0 ? 'Semaine' : 'Week-end') + (jt ? ' (' + dateCourte(jt.date) + ')' : ''), x + 132, haut - 15.5, { taille: 12, centre: true })
        + rect(x, haut - 8, 264.1, 1.2, 'background:' + DEGRADE)
        + image(images[i * 2 + j], x, haut, 264.1, HAUTEUR_JOUR_TYPE)
    }).join('')
  }).join('')
  return page('page-9', sousTitre('Journées types par saison', 28.3, 40.9)
    + (d.journeesTypes && d.journeesTypes.length
      ? texte('Jour réel le plus proche de la journée moyenne de chaque saison (hiver : décembre à février ; printemps : mars à mai ; été : juin à août ; automne : septembre à novembre).', 29, 70.5, { taille: 8.5, couleur: '#666', graisse: 300 })
      : '')
    + legendeEnLigne(legende, 29, 83.7, 537.9, 10, 'entre')
    + blocs + piedDePage(9, true))
}

function valeurAmortissement(r: any, cle: string): number | null {
  if (cle === 'emprunt' || cle === 'maintenance' || cle === 'assurance') return r[cle] > 0 ? -r[cle] : null
  return r[cle] == null ? null : r[cle]
}
function classeValeur(cle: string, v: number | null): string {
  if (v === null) return 'vide'
  if (cle === 'resultat' || cle === 'balance' || cle === 'economie') return v >= 0 ? 'positif' : 'negatif'
  if (cle === 'emprunt' || cle === 'maintenance' || cle === 'assurance') return 'charge'
  return ''
}

/** Page 10 — « 4. ANALYSE FINANCIÈRE » (détail du bilan des frais comme le bloc CAPEX de l'écran). */
export function pageAnalyseFinanciere(d: PdfReportData, images: { projection?: string; donutEconomies?: string }, numero?: number): string {
  var m = echapper(mention(d))
  var ligne = function (l: { libelle: string; valeur: string; total?: boolean; filet?: boolean }) {
    return '<div class="ligne' + (l.total ? ' total' : '') + (l.filet ? ' filet' : '') + '"><span class="lib">' + echapper(l.libelle)
      + '</span><span class="val">' + echapper(l.valeur) + '</span></div>'
  }
  var legende = [{ couleur: C_YEL, texte: 'Autoconso. directe — ' + eurHtml(d.ecoAutoconsoDir) }]
  if (d.hasBattery) legende.push({ couleur: C_ORA, texte: 'Via batterie — ' + eurHtml(d.ecoBat) })
  if (!d.zeroInjection) legende.push({ couleur: C_GRN, texte: 'Vente de surplus — ' + eurHtml(d.rachatSurplus) })
  var dim = dimensionsPage10(d)
  return page('page-' + (numero || 10), titreSection('4. ANALYSE FINANCIÈRE', true)
    + '<div class="flux remplir" style="top:66pt">'
    + '<section><h2>Bilan des frais<span class="mention">' + m + '</span></h2><div class="cadre">'
    + lignesBilanFrais(d).map(ligne).join('') + '</div></section>'
    + '<section><h2>Financement</h2><div class="cadre">' + lignesFinancement(d).map(ligne).join('') + '</div></section>'
    + '<section><div class="titre-projection"><h2>Projection financière sur 30 ans en €<span class="mention">' + m + '</span></h2>'
    + '<span class="option">' + (d.avecFinancement ? 'Avec Financement' : 'Sans Financement') + '</span></div>'
    + (images.projection ? '<img class="graphique" style="height:' + dim.projection + 'pt" src="' + images.projection + '" alt="">' : '') + '</section>'
    + '<section class="economies"><h2>Détail des économies (an 1)<span class="mention">' + m + '</span></h2>'
    + '<p class="total-eco">Total : ' + echapper(eurHtml(d.economiesAn1)) + '</p>'
    + '<div class="corps">' + (images.donutEconomies ? '<img style="width:' + dim.donut + 'pt;height:' + dim.donut + 'pt" src="' + images.donutEconomies + '" alt="">' : '') + '<ul class="legende">'
    + legende.map(function (l) { return '<li><span class="pastille" style="background:' + l.couleur + '"></span>' + echapper(l.texte) + '</li>' }).join('')
    + '</ul></div></section>'
    + '</div>' + piedDePage(numero || 10, true))
}

/** Page 11 — tableau d'amortissement en texte. */
export function pageAmortissement(d: PdfReportData, numero?: number): string {
  var cols = colonnesAmortissement(d)
  var entetes = '<th>Année</th>' + cols.map(function (c) { return '<th>' + echapper(c.titre) + '</th>' }).join('')
  var ligneHtml = function (ligne: any) {
    var classe = ligne.isTotal ? 'total' : ligne.isRoiYear ? 'rentable' : ''
    return '<tr class="' + classe + '"><td>' + echapper(ligne.label) + '</td>' + cols.map(function (c) {
      var v = valeurAmortissement(ligne, c.cle)
      // coûts d'électricité sans signe, comme à l'écran (16/10/2026)
      var texteValeur = v !== null && (c.cle === 'cashAvant' || c.cle === 'cashApres') ? eurHtml(v) : eurSigne(v)
      return '<td class="' + classeValeur(c.cle, v) + '">' + echapper(texteValeur) + '</td>'
    }).join('') + '</tr>'
  }
  var rows: any[] = d.amortRows || []
  var tableau = function (lignes: any[]) {
    return '<table><thead><tr>' + entetes + '</tr></thead><tbody>' + lignes.map(ligneHtml).join('') + '</tbody></table>'
  }
  // études sur 30 ans (27/10/2026) : deux tableaux, total et années 1 à 20, puis années 21 à 30, comme à l'écran
  var tableaux = rows.length > 21
    ? '<p class="titre-partie">Années 1 à 20</p>' + tableau(rows.slice(0, 21))
      + '<p class="titre-partie">Années 21 à ' + (rows.length - 1) + '</p>' + tableau(rows.slice(21))
    : tableau(rows)
  return page('page-' + (numero || 11), sousTitre('Détail de l’amortissement annuel', 28.3, 40.9, mention(d))
    + '<div class="flux' + (rows.length > 21 ? ' amort-30' : '') + '" style="top:56pt">'
    + tableaux
    + '<p class="note">Ligne verte : année où les économies cumulées couvrent l’investissement. Économies indexées sur l’inflation de l’électricité, '
    + 'production réduite chaque année par la dégradation des panneaux et de la batterie.</p>'
    + '</div>' + piedDePage(numero || 11, true))
}

/** Annexe — détail de l'investissement d'une étude chiffrée au matériel (27/10/2026). */
export function pageDetailInvestissement(d: PdfReportData, numero: number): string {
  if (d.sectionsCapex && d.sectionsCapex.length) return pageDetailSections(d, numero)
  var bloc = function (titre: string, poste: string, total: number) {
    var lignes = (d.detailMateriel || []).filter(function (l) { return l.poste === poste })
    if (!lignes.length) return ''
    return '<section><h2>' + echapper(titre) + '</h2><div class="cadre">'
      + lignes.map(function (l) {
        return '<div class="ligne"><div class="lib">' + echapper(l.libelle) + '</div><div class="val">' + eurHtml(l.montant) + '</div></div>'
      }).join('')
      + '<div class="ligne total filet"><div class="lib">Total</div><div class="val">' + eurHtml(total) + '</div></div>'
      + '</div></section>'
  }
  return page('page-' + numero, sousTitre('Annexe — détail de l’investissement', 28.3, 40.9, mention(d))
    + '<div class="flux" style="top:56pt">'
    + (d.typePro ? '<p class="note" style="margin:0 0 8pt">Type de client : ' + echapper(d.typePro) + '</p>' : '')
    + bloc('Installation PV' + (d.puissanceKwc > 0 ? ' (' + kwcTexte(d.puissanceKwc) + ')' : ''), 'PV', d.coutInstallationPV || 0)
    + bloc('Installation batterie' + (d.batteryKwh > 0 ? ' (' + Math.round(d.batteryKwh) + ' kWh)' : ''), 'BATTERIE', d.coutInstallationBatterie || 0)
    + '<p class="note">Prix de vente du matériel et de la main d’œuvre, chiffrés d’après le catalogue du matériel. '
    + 'Câble, datalogger, raccordement et travaux : voir l’analyse financière (page 10).</p>'
    + '</div>' + piedDePage(numero, true))
}

/**
 * Annexe par section du CAPEX (03/11/2026) : détail de chaque section (prix de vente), matériel retenu, garanties des
 * panneaux et des onduleurs, câble onduleur ↔ TGBT (section et longueur). Ni prix de revient ni marge.
 */
export function pageDetailSections(d: PdfReportData, numero: number): string {
  var html = ''
  var detail = d.detailMateriel || []
  ;(d.sectionsCapex || []).forEach(function (sec) {
    var lignes = detail.filter(function (l) { return l.poste === sec.section && !l.section })
    html += '<section><h2>' + echapper(sec.libelle) + '</h2><div class="cadre">'
      + lignes.map(function (l) {
        return '<div class="ligne"><div class="lib">' + echapper(l.libelle) + '</div><div class="val">' + eurHtml(l.montant) + '</div></div>'
      }).join('')
      + '<div class="ligne total filet"><div class="lib">Total</div><div class="val">' + eurHtml(sec.montant) + '</div></div>'
      + '</div></section>'
  })
  var resume = d.resumeMateriel || {}
  var cles = Object.keys(resume)
  if (cles.length) {
    html += '<section><h2>Matériel retenu</h2><div class="cadre">'
      + cles.map(function (k) { return '<div class="ligne"><div class="lib">' + echapper(k) + '</div><div class="val">' + echapper(resume[k]) + '</div></div>' }).join('')
      + '</div></section>'
  }
  if (d.garanties && d.garanties.length) {
    html += '<section><h2>Garanties</h2><div class="cadre">'
      + d.garanties.map(function (g) { return '<div class="ligne"><div class="lib">' + echapper(g) + '</div></div>' }).join('')
      + '</div></section>'
  }
  return page('page-' + numero, sousTitre('Annexe — détail de l’investissement', 28.3, 40.9, mention(d))
    + '<div class="flux" style="top:56pt">'
    + (d.typePro ? '<p class="note" style="margin:0 0 8pt">Type de client : ' + echapper(d.typePro) + '</p>' : '')
    + html
    + '<p class="note">Prix de vente du matériel, des prestations et de la main d’œuvre, chiffrés d’après le catalogue du matériel.</p>'
    + '</div>' + piedDePage(numero, true))
}

/** Page 12 — « 5. IMPACT SUR LES FACTURES ». */
export function pageFactures(d: PdfReportData, im: ImagesRapport, mesure?: Mesure): string {
  var m = mention(d)
  var lignes = lignesBilanAnnuel(d).map(function (l, i) {
    var haut = 86.8 + i * 22.7
    var gras = l.niveau !== 'ligne' && l.niveau !== 'sous'
    var fond = l.niveau === 'groupe' ? rect(28.3, haut, 538.6, 22.3, 'background:#e8f2e7')
      : l.niveau === 'total' ? rect(28.3, haut, 538.6, 22.3, 'background:#9fcdd1')
      : rect(28.3, haut + 22, 538.6, 0.6, 'background:#e6e6e6')
    var x = l.niveau === 'groupe' ? 46.5 : l.niveau === 'sous' ? 55 : 32.3
    return fond + texte(echapper(l.libelle), x, haut + 15.2, { taille: 11, graisse: gras ? 600 : 400, couleur: l.niveau === 'sous' ? '#757575' : undefined })
      + caseValeur(l.valeur, 437.5, haut, 129.4, 22.3, mesure, { graisse: gras ? 600 : 400 })
  }).join('')
  return page('page-12', titreSection('5. IMPACT SUR LES FACTURES', true)
    + sousTitre('Bilan annuel estimé', 28.3, 76.0, m)
    + lignes
    + sousTitre('Factures mensuelles (€)', 28.3, 305.5, m)
    + legendeEnLigne([{ texte: 'Facture avant (€)', couleur: C_GRY }, { texte: 'Facture après (€)', couleur: C_YEL }], 266.9, 303.7, 300, 10, 'droite', 30)
    + image(im.factures, 28.3, 315.7, 538.6, 201.2)
    + sousTitre('Demande sur le réseau (kWh)', 28.3, 554.5)
    + legendeEnLigne([{ texte: 'Soutirage avant (kWh)', couleur: C_GRY, anneau: true }, { texte: 'Soutirage après (kWh)', couleur: C_YEL, anneau: true }], 266.9, 553.1, 300, 10, 'droite', 30)
    + image(im.reseau, 28.3, 564.5, 538.6, 201.2)
    + piedDePage(12, true))
}

var LIMITES = [
  'Cette étude comprend la fourniture, l’installation et la connexion du matériel ;',
  'Cette étude ne comprend pas les frais relatifs à une étude de structure ;',
  'Cette étude ne comprend pas le coût du raccordement au RPD (celui-ci est à la charge du producteur) ;',
  'Cette étude ne comprend pas le redressage/renforcement éventuel de charpente ;',
  'Cette étude ne comprend pas le démontage de toiture amiantée ;',
  'Cette étude ne comprend pas la réalisation de tranchées pour le passage de gaines ou fourreaux ni la fourniture des fourreaux en question ;',
  'Cette étude comprend la liaison onduleur ↔ TGBT (sous réserve de validation technique) ;',
  'Cette étude ne comprend pas les frais relatifs au déplacement de votre soutirage en limite de propriété ;',
  'Cette étude ne tient pas compte d’un remplacement éventuel de l’onduleur et/ou de la batterie au cours des 30 ans, qui sera probablement nécessaire ;',
  'Cette étude est une pré-étude simplifiée d’avant-projet, établie à partir des informations transmises et de données de production et de consommation estimées : elle devra être confirmée par une étude détaillée de notre bureau d’études ;',
  'La validité de cette étude est de 1 mois.',
]

/** Page 13 — contrats de raccordement et d'achat, limites de prestations. */
export function pageContrats(im: ImagesRapport): string {
  var puce = function (t: string) {
    return '<li>' + (im.puce ? '<img src="' + im.puce + '" alt="">' : '') + '<span>' + echapper(t) + '</span></li>'
  }
  return page('page-13', titreSection('6. CONTRATS DE RACCORDEMENT AU RÉSEAU ET D’ACHAT', true)
    + '<div class="flux texte-courant" style="top:69pt">'
    + '<p>Pour injecter votre production dans le Réseau Public de Distribution électrique, il sera nécessaire de souscrire deux contrats :</p>'
    + '<ul class="puces">' + puce('Un contrat de raccordement au Réseau Public de Distribution (ENEDIS ou Régie locale)')
    + puce('Un contrat d’achat de votre production avec EDF-OA (ou autre acheteur)') + '</ul>'
    + '<p class="inclus">L’ensemble des démarches administratives est inclus dans notre offre (déclaration de travaux, demande de raccordement et de contrat d’achat).</p>'
    + '</div>'
    + '<div class="a" style="left:0;top:177pt;width:100%">' + titreSection('7. LIMITES DE PRESTATIONS', true) + '</div>'
    + '<div class="flux texte-courant" style="top:254pt"><ul class="puces">' + LIMITES.map(puce).join('') + '</ul></div>'
    + piedDePage(13, true))
}

/** Page 14 — « NOS AGENCES EN FRANCE » (carte du gabarit). */
export function pageAgences(im: ImagesRapport): string {
  var blocs = AGENCES.map(function (a, i) {
    var base = 476.8 + i * 103.3
    return texte(echapper(a.nom), 28.3, base, { taille: 12, graisse: 600, couleur: CYAN })
      + a.lignes.map(function (l, j) { return texte(echapper(l), 28.3, base + 14.4 * (j + 1), { taille: 12, graisse: 300 }) }).join('')
  }).join('')
  return page('page-14', image(im.fondAgences, 0, 0, LARGEUR_PAGE, HAUTEUR_PAGE)
    + titreSection('NOS AGENCES EN FRANCE', false) + blocs + piedDePage(14, true))
}

// ── Feuille de style ──────────────────────────────────────────────────────
function styles(p: PolicesRapport): string {
  var police = function (graisse: number, b64: string | undefined) {
    return b64 ? '@font-face{font-family:Poppins;font-weight:' + graisse + ';src:url(data:font/ttf;base64,' + b64 + ') format("truetype")}' : ''
  }
  return police(300, p.light || p.regular) + police(400, p.regular) + police(500, p.medium) + police(600, p.semibold)
    + police(700, p.bold || p.semibold) + police(800, p.extrabold || p.bold || p.semibold)
    + '@page{size:595.28pt 841.89pt;margin:0}'
    + '*{box-sizing:border-box;margin:0;padding:0}'
    + 'html,body{font-family:Poppins,sans-serif;color:#000;-webkit-print-color-adjust:exact;print-color-adjust:exact}'
    + '.page{position:relative;width:595.28pt;height:841.89pt;overflow:hidden;page-break-after:always;break-after:page}'
    + '.page:last-child{page-break-after:auto;break-after:auto}'
    + '.a{position:absolute;white-space:nowrap;line-height:1}'
    + 'img.a{display:block}'
    + '.mention{margin-left:7pt}'
    + 'h1.a,h2.a{margin:0}'
    + '.titre-couverture{line-height:1.2;font-weight:400}'
    + '.cadre-abs{border:2pt solid transparent;border-radius:13pt;background:linear-gradient(#fff,#fff) padding-box,' + DEGRADE + ' border-box}'
    + '.legende-ligne{display:flex;font-weight:300;white-space:nowrap}'
    + '.legende-ligne .item{display:flex;align-items:center}'
    + '.pastille-legende{display:inline-block;width:9pt;height:9pt;border-radius:50%;margin-right:5.5pt;flex:none}'
    + '.pastille-legende.anneau{border:1.6pt solid;border-radius:2.5pt;background:#fff}'
    + '.trait-legende{display:inline-block;width:18pt;height:0;border-top:2pt dashed;margin-right:5.5pt;flex:none}'
    + '.alerte{white-space:normal;line-height:1.3;font-size:8.5pt;padding:5pt 8pt 5pt 12pt;background:#fff8e8;color:#6b4a00;border-left:3pt solid #f5a623;display:flex;align-items:center}'
    + '.alerte.forte{background:#fff0e0;color:#7a2e00;border-left:5pt solid #e65100;font-weight:600}'
    // sommaire
    + '.sommaire{position:absolute;left:28.3pt;right:28.3pt;top:79pt}'
    + '.sommaire a{display:flex;align-items:baseline;color:#000;text-decoration:none}'
    + '.som-titre{font-size:14pt;font-weight:600;height:21.6pt;justify-content:space-between;margin-top:12.2pt}'
    + '.som-titre.premier{margin-top:0}'
    + '.som-sous{font-size:12pt;font-weight:500;height:20.05pt;padding-left:28.4pt}'
    + '.som-sous .points{flex:1;border-bottom:1pt dotted #555;margin:0 2pt;transform:translateY(-3pt)}'
    // pages en flux (analyse financière, amortissement, contrats)
    + '.flux{position:absolute;left:28.3pt;right:28.3pt}'
    + '.flux h2{font-size:12pt;font-weight:500;margin:0 0 5pt}'
    + '.flux section{margin-bottom:10pt}'
    + '.cadre{border:2pt solid transparent;border-radius:12pt;background:linear-gradient(#fff,#fff) padding-box,' + DEGRADE + ' border-box;padding:5pt 0}'
    + '.ligne{display:flex;align-items:stretch;font-size:10.5pt;line-height:1.25}'
    + '.ligne .lib,.ligne .val{display:flex;align-items:center;min-height:17pt}'
    + '.ligne .lib{flex:0 0 345pt;padding-left:17.7pt;padding-right:8pt}'
    + '.ligne .val{flex:1;border-left:1.4pt solid ' + BLEU + ';padding-left:16pt;white-space:nowrap}'
    + '.ligne.total{font-weight:600}'
    + '.ligne.filet{margin-top:4pt;padding-top:4pt;border-top:.6pt solid #cfd8e3}'
    // page 10 : du titre au pied de page, place restante répartie entre les blocs (dimensionsPage10)
    + '.flux.remplir{height:706pt;display:flex;flex-direction:column;justify-content:space-between}'
    + '.flux.remplir section{margin:0}'
    + '.remplir .ligne{font-size:11.5pt}'
    + '.remplir .ligne .lib,.remplir .ligne .val{min-height:' + HAUTEUR_LIGNE_PAGE10 + 'pt}'
    + '.remplir .economies .total-eco{font-size:12pt}'
    + '.remplir .legende{font-size:11pt;gap:12pt}'
    + '.titre-projection{display:flex;align-items:baseline;gap:18pt}'
    + '.titre-projection .option{font-size:8pt;font-weight:400}'
    + '.graphique{display:block;width:539pt}'
    + '.economies .total-eco{font-size:11pt;margin:0 0 2pt}'
    + '.economies .corps{display:flex;align-items:center;gap:40pt}'
    + '.economies img{width:96pt;height:96pt}'
    + '.legende{list-style:none;font-size:10pt;display:grid;gap:9pt}'
    + '.legende li{display:flex;align-items:center;gap:8pt}'
    + '.pastille{width:9pt;height:9pt;border-radius:50%;display:inline-block}'
    + 'table{width:100%;border-collapse:collapse;font-size:8.6pt;font-variant-numeric:tabular-nums}'
    + 'thead th{background:#1565c0;color:#fff;font-weight:600;padding:6pt 4pt;text-align:right;line-height:1.15}'
    + 'thead th:first-child{text-align:left;border-radius:6pt 0 0 0}'
    + 'thead th:last-child{border-radius:0 6pt 0 0}'
    + 'tbody td{padding:4.4pt 4pt;text-align:right;border-bottom:.5pt solid #e0e0e0;white-space:nowrap}'
    + 'tbody td:first-child{text-align:left;border-right:.8pt solid #bdbdbd}'
    + 'tbody tr:nth-child(even) td{background:#f7f7f7}'
    + 'tbody tr.total td{background:#d6e8f8;font-weight:700;font-size:9.6pt;padding-top:6pt;padding-bottom:6pt;border-bottom:1.4pt solid #1565c0}'
    + 'tbody tr.rentable td{background:#e8f5e9;font-weight:600}'
    + 'td.positif{color:#2e7d32}td.negatif{color:#c62828}td.charge{color:#b71c1c}td.vide{color:#9e9e9e}'
    + '.note{font-size:8pt;color:#555;margin-top:8pt;line-height:1.4}'
    // études sur 30 ans : deux tableaux sur la page 11, lignes resserrées (27/10/2026)
    + '.titre-partie{font-size:9pt;font-weight:700;color:#1565c0;margin:6pt 0 3pt}'
    + '.amort-30 tbody td{padding-top:2pt;padding-bottom:2pt}'
    + '.amort-30 tbody tr.total td{padding-top:3pt;padding-bottom:3pt}'
    // texte courant (contrats, limites)
    + '.texte-courant{font-size:12pt;line-height:14.4pt}'
    + '.texte-courant p{text-align:justify;margin-bottom:5.7pt}'
    + '.texte-courant .inclus{font-weight:600;color:' + CYAN + '}'
    + '.puces{list-style:none;margin:0 0 5.7pt}'
    + '.puces li{display:flex;align-items:flex-start;text-align:justify;margin-bottom:5.7pt}'
    + '.puces img{width:9.4pt;height:10.1pt;margin:2.3pt 8pt 0 3pt;flex:none}'
}

// ── Document complet ──────────────────────────────────────────────────────
/** Rapport complet : 14 pages. */
export function documentRapport(d: PdfReportData, images: ImagesRapport, polices: PolicesRapport, mesure?: Mesure): string {
  var pages = [
    pageCouverture(d, images, mesure),
    pageSommaire(d),
    pagePresentation(images),
    pageProjet(d, images, mesure),
    pageSite(d, images, mesure),
    pageInstallation(d, images, mesure),
    pageEnergetique(d, images, mesure),
    pageFlux(images),
    pageJoursTypes(d, images, mesure),
    pageAnalyseFinanciere(d, images, 10),
    pageAmortissement(d, 11),
    pageFactures(d, images, mesure),
    pageContrats(images),
    pageAgences(images),
  ]
  // chiffrage au matériel (27/10/2026) : détail de l'investissement en annexe, après « Nos agences » (numéros inchangés)
  if (d.detailMateriel && d.detailMateriel.length) pages.push(pageDetailInvestissement(d, 15))
  var titre = 'Étude photovoltaïque' + (d.projectName ? ' — ' + d.projectName : '')
  return '<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>' + echapper(titre) + '</title><style>' + styles(polices)
    + '</style></head><body>' + pages.join('') + '</body></html>'
}

/** Pages 10 et 11 seules (essai du 15/10/2026), gardé pour les tests. */
export function documentPagesFinancieres(d: PdfReportData, images: ImagesPagesFinancieres, polices: PolicesRapport, premierNumero: number): string {
  return '<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Analyse financière</title><style>' + styles(polices) + '</style></head><body>'
    + pageAnalyseFinanciere(d, images, premierNumero) + pageAmortissement(d, premierNumero + 1) + '</body></html>'
}
